import { SetStateAction, useCallback, useEffect, useState } from 'react';
import { noop } from '@fabric-ui/utils';

const resolveState = <T>(state: SetStateAction<T>, prev: T) => (typeof state === 'function' ? (state as (prevState: T) => T)(prev) : state);

export interface HistoryState<T> {
  history: T[];
  currentStep: number;
}

interface UseStateWithHistoryOptions<T> {
  allowKeyboardShortcuts?: boolean;
  onUndo?: (nextState?: T) => void;
  onRedo?: (nextState?: T) => void;
}

const isHistoryState = <T>(state: any): state is HistoryState<T> => Array.isArray(state?.history) && typeof state?.currentStep === 'number';

export const useStateWithHistory = <T>(initialState: T | HistoryState<T>, options?: UseStateWithHistoryOptions<T>) => {
  const [history, setHistory] = useState<T[]>(isHistoryState(initialState) ? initialState.history : [initialState as T]);
  const [currentStep, setCurrentStep] = useState(isHistoryState(initialState) ? initialState.currentStep : 0);

  const setState = useCallback(
    (newState: SetStateAction<T>) => {
      const newHistory = [...history.slice(0, currentStep + 1), resolveState(newState, history[currentStep])];
      setHistory(newHistory);
      setCurrentStep(newHistory.length - 1);
    },
    [history, currentStep]
  );

  const clearHistory = useCallback(
    (newState: SetStateAction<T> = s => s) => {
      setHistory([resolveState(newState, history[currentStep])]);
      setCurrentStep(0);
    },
    [history, currentStep]
  );

  const undo = useCallback(() => {
    if (currentStep > 0) {
      options?.onUndo?.(history[currentStep - 1]);
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, history, options?.onUndo]);

  const redo = useCallback(() => {
    if (currentStep < history.length - 1) {
      options?.onRedo?.(history[currentStep + 1]);
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, history, options?.onRedo]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };

    if (options?.allowKeyboardShortcuts) {
      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }

    return noop;
  }, [undo, redo, options?.allowKeyboardShortcuts]);

  return {
    currentState: history[currentStep],
    setState,
    undo,
    canUndo: currentStep > 0,
    redo,
    canRedo: currentStep < history.length - 1,
    clearHistory,
    isInitialState: currentStep === 0,
    history,
    currentStep,
  };
};
